"use client";

import { useEffect } from "react";

import { captureEvent } from "@/lib/analytics";

/**
 * Landing PostHog view instrumentation — a single IntersectionObserver
 * (mirrors `FacebookPixelViewContent`) that fires once per element as the
 * pricing block / pricing cards scroll into view. Anonymous funnel signal
 * between pageview and checkout_click.
 *
 * Section-level `section_viewed` is gated off by default (noisy); flip
 * ENABLE_SECTION_VIEWED to capture it.
 */
const ENABLE_SECTION_VIEWED = false;

export function PostHogViews() {
  useEffect(() => {
    const fired = new Set<Element>();

    const selector = ENABLE_SECTION_VIEWED
      ? "[data-ph-pricing], [data-ph-price-card], section[id]"
      : "[data-ph-pricing], [data-ph-price-card]";

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          if (fired.has(el)) {
            io.unobserve(el);
            continue;
          }
          fired.add(el);

          if (el.matches("[data-ph-price-card]")) {
            captureEvent("pricing_card_viewed", {
              plan: el.getAttribute("data-ph-plan"),
              tab: el.getAttribute("data-ph-tab"),
            });
          } else if (el.matches("[data-ph-pricing]")) {
            captureEvent("pricing_viewed", {});
          } else if (ENABLE_SECTION_VIEWED) {
            captureEvent("section_viewed", { section_id: el.id });
          }

          io.unobserve(el);
        }
      },
      { threshold: 0.3 },
    );

    // Elements may mount after this component. Query immediately and again on
    // the next frame so late-rendered CMS cards still get observed.
    const observeAll = () => {
      document.querySelectorAll(selector).forEach((el) => {
        if (!fired.has(el)) io.observe(el);
      });
    };
    observeAll();
    const raf = requestAnimationFrame(observeAll);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, []);

  return null;
}

export default PostHogViews;
